import type {
  FetchOptions,
  ProblemDetails
} from '@/scripts/Common/api/types';

import { getConfig } from '@/plugins/config';

import {
  responseType,
} from "@/scripts/Common/api/enums";

import {
  ErrorCodes
} from '@/Enums';

import {
  isAbortError
} from '@/composables';

import {
  isTimeoutError
} from '../HelperFunctions';

import {
  isProblemDetails
} from './guards';

import {
  HttpError
} from './classes';

import { useAuth } from '@/composables/useAuth';
import { useGlobalSnackbar } from '@/composables/useGlobalSnackbar';
import router, { Routes } from '@/router';
import i18n from '@/i18n';

// Guards against multiple concurrent 401 responses each triggering their own logout+redirect.
// When several requests are in-flight simultaneously and the token expires, all of them receive
// a 401. Without this flag, each would call logout() and router.push() independently, causing
// the redirectTo URL to be built from an already-redirected route (double-nesting like
// /login?redirectTo=/login?redirectTo=/customers/...).
// The flag is reset in LoginView.vue after a successful login, since this is a SPA and the
// module stays alive across navigations — it does NOT reset automatically on page load.
let sessionExpiredHandling = false;

const apiNameMap = {
  appSettings: '/api/v1/admin/settings/app',
  deleteCards: '/api/v1/admin/card/delete',
  getCardList: '/api/v1/admin/card/list',
  getUnusedCardNumbers: '/api/v1/admin/card/unused-cardnumbers',
  getUnassignedCardNumbers: '/api/v1/admin/issuer/card/unassigned',
  updateOrCreateCards: '/api/v1/admin/card',
  getIssuerCardList: '/api/v1/admin/issuer/card/list',
  updateOrCreateIssuerCards: '/api/v1/admin/issuer/card',
  deleteIssuerCards: '/api/v1/admin/issuer/card/delete',
  importCards: '/api/v1/admin/import/cards',
  importCardReferences: '/api/v1/admin/import/issuer/cards',
  reassignCardsToNewAccounts: '/api/v1/admin/card/reassign-to-new-accounts',
  getConfiguredWebchargesByIssuerId: '/api/v1/admin/webcharges/configured/issuer/list',
  deleteIssuerWebcharges: '/api/v1/admin/webcharges/configured/delete',
  configuredWebchargeResource: '/api/v1/admin/webcharges/configured',
  webchargeConfiguration: '/api/v1/admin/webcharge/configuration/issuer',
  cardLimiteConfiguration: '/api/v1/admin/card/limite',
  gratificationConfiguration: '/api/v1/admin/webcharge/configuration/gratification',
  getWebchargeStates: '/api/v2/admin/webcharge/protocol/state/list',
  repeatLastWebcharge: '/api/v1/admin/webcharge/protocol/{protocolId}/repeat',
  sepaTerminalSettingsResource: '/api/v1/admin/sepa/configuration/terminal',
  sepaOtherSettingsResource: '/api/v1/admin/sepa/configuration/other',
  sepaCustomerChargeSettingsResource: '/api/v1/admin/sepa/configuration/customer/charges',
  sepaIssuerSettingsResource: '/api/v1/admin/sepa/configuration/issuer',
  sepa: '/api/v1/admin/sepa',
  sepaDownload: '/api/v1/admin/sepa/{sepaExportId}/download',
  sepaSaleReset: '/api/v1/admin/sepa/{sepaExportId}/reset',
  sepaWebchargeReset: '/api/v1/admin/sepa/webcharges/{sepaExportId}/reset',
  cardUser: '/api/v1/admin/card-users',
  customers: '/api/v1/admin/customers',
  customersV2: '/api/v2/admin/customers',
  customerResource: '/api/v1/admin/customers/{customerId}',
  customerStateChange: '/api/v1/admin/customers/{action}',
  defaultProvision: '/api/v1/admin/customers/default-provision',
  customderDeviceAuthConfiguration: '/api/v1/admin/customers/{customerId}/devices/{deviceId}/configuration/authentication',
  customerDeviceConfiguration: '/api/v1/admin/customers/{customerId}/devices/{deviceId}/configuration/device',
  customerApiKeyPermissions: '/api/v1/admin/customers/{customerId}/apiKeys/{apiKeyId}/permissions',
  importCustomers: '/api/v1/admin/import/customers',
  service: '/api/v1/admin/service',
  issuers: '/api/v1/admin/issuers',
  issuersV2: '/api/v2/admin/issuers',
  issuersWithParams: '/api/v1/admin/issuers/{param}',
  passwordReset: '/api/v1/admin/account/{group}/{id}/password/reset',
  operator: '/api/v1/admin/operator',
  creditorIdValidation: '/api/v1/admin/bank-account/validate/creditor-id',
  accountBalanceReport: '/api/v1/admin/report/account-balance-report/{deadline}',
  chargeLimitReport: '/api/v1/admin/report/issuer/chargelimit-report',
  chargeReport: '/api/v1/admin/report/issuer/webcharge-report',
  customerChargeReport: '/api/v1/admin/report/customer/charge-report',
  salesReport: '/api/v1/admin/report/sales',
  vatDetails: '/api/v1/admin/vat-details',
  issuerIdentityUsers: '/api/v1/admin/issuers/{issuerId}/identity/users',
  identityUsers: '/api/v1/admin/identity/users/{userId}',
  changeIdentityUsersState: '/api/v1/admin/identity/users/{userId}/{state}',
  identityUserCreatePasswordChangeTicket: '/api/v1/admin/identity/users/{userId}/password/change/ticket',
  identityUserResetPasswordViaEmail: '/api/v1/admin/identity/users/{userId}/password/change/email',
  login: '/api/v2/admin/login',
  chargingDaysSettings: '/api/v1/admin/webcharge/configuration/issuer',
  gratificationSettings: '/api/v1/admin/webcharge/configuration/gratification',
  accountLimitSettings: '/api/v1/admin/settings/account/limits',
  identityUserType: '/api/v1/admin/identity/users/{userId}/{userType}',
  changeIdentityUserTypeState: '/api/v1/admin/identity/users/{userId}/{userType}/{state}',
  pointOfSales: '/api/v1/admin/customers/{customerId}/pos',
  pointOfSale: '/api/v1/admin/customers/{customerId}/pos/{pointOfSaleId}',
  devices: '/api/v1/admin/devices',
  device: '/api/v1/admin/devices/{deviceId}',
  devicesImport: '/api/v1/admin/devices/import',
  customerDevices: '/api/v1/admin/customers/{customerId}/devices',
  customerDevice: '/api/v1/admin/customers/{customerId}/devices/{deviceId}',
  customerDeviceApiKey: '/api/v1/admin/customers/{customerId}/devices/{deviceId}/apiKey',
  customerApiKeys: '/api/v1/admin/customers/{customerId}/apiKeys',
  customerApiKeysExternal: '/api/v1/admin/customers/{customerId}/apiKeys/devices/external',
  customerApiKey: '/api/v1/admin/customers/{customerId}/apiKeys/{apiKeyId}',
  customerApiKeyRotate: '/api/v1/admin/customers/{customerId}/apiKeys/{apiKeyId}/rotate',
  devicesUnassigned: '/api/v1/admin/devices/unassigned',
  accountGroup: '/api/v1/admin/account-groups/{id}',
  accountGroups: '/api/v1/admin/account-groups',
  createAccount: '/api/v1/admin/accounts',
  accountsList: '/api/v1/admin/accounts/list',
  accountActivate: '/api/v1/admin/accounts/{accountNumber}/activate',
  accountDeactivate: '/api/v1/admin/accounts/{accountNumber}/deactivate',
  accountLock: '/api/v1/admin/accounts/{accountNumber}/lock',
  accountClose: '/api/v1/admin/accounts/{accountNumber}/close',
  accountChangeAccountGroup: '/api/v1/admin/accounts/{accountNumber}/account-group',
  accountStatusHistory: '/api/v1/admin/accounts/{accountNumber}/history',
  accountCorrection: '/api/v1/admin/accounts/correction',
  accountTransfer: '/api/v1/admin/accounts/transfer',
  merchantTransactionsList: '/api/v1/admin/report/transactions/merchant/list',
  dailyTotalsList: '/api/v1/admin/report/accounting/daily-totals/list',
  transactionsList: '/api/v1/admin/report/transactions/list',
  issuerTransactionsList: '/api/v1/admin/report/transactions/issuer/list',
  transactionsExport: '/api/v1/admin/report/transactions/export',
  merchantTransactionsExport: '/api/v1/admin/report/transactions/merchant/export',
  issuerTransactionsExport: '/api/v1/admin/report/transactions/issuer/export',
  transactionReceiptByNumber: '/api/v1/admin/report/transactions/{transactionNumber}/receipt',
  customerAuthCards: '/api/v1/admin/customers/cards',
  customerAuthCardsCreate: '/api/v1/admin/customers/{customerId}/cards',
  customerAuthCard: '/api/v1/admin/customers/{customerId}/cards/{cardNumber}',
  customerAuthCardBlock: '/api/v1/admin/customers/{customerId}/cards/{cardNumber}/block',
  customerAuthCardUnblock: '/api/v1/admin/customers/{customerId}/cards/{cardNumber}/unblock',
  schedulerJobs: '/api/v1/admin/scheduler-jobs',
};

Object.freeze(apiNameMap);

const replacePlaceholders = (input: string, replacements: Record<string, string>) => {
  return input.replace(/{(\w+)}/g, (text, key) => replacements[key] || text);
}

const createFetchDefaults = async ({ apiName, options = {} }: { apiName: keyof typeof apiNameMap, options: FetchOptions }) => {
  const config = await getConfig();
  const gatewayUrl = config.GatewayUrl;

  let route = apiNameMap[apiName];
  if (options.routeParams) {
    route = replacePlaceholders(route, options.routeParams);
  }

  const url = new URL(`${gatewayUrl}${route}`);

  const {
    method = 'GET',
    query = undefined,
  } = options;

  if (
    query !== null
    && typeof query === 'object'
    && !Array.isArray(query)
  ) {

    for (const [key, value] of Object.entries(query)) {
      //.net apis require query arrays in the following format: value=element1&value=element2&value=element3
      if (Array.isArray(value)) {
        value.forEach(element => url.searchParams.append(key, element))
        continue;
      }

      if ((value !== undefined && value !== null)) {
        // @ts-ignore
        url.searchParams.set(key, value)
      }
    }
  }

  return {
    method,
    url,
  }
};

const createProblemDetails = (error: unknown): ProblemDetails => {
  if (isTimeoutError(error)) {
    return {
      status: 504,
      type: ErrorCodes.client_request_timeout,
      title: "Request Timeout",
      detail: error instanceof Error ? error.message : "Network connection failed due to timeout"
    };
  }

  if (isAbortError(error)) {
    return {
      status: 400,
      type: ErrorCodes.client_request_aborted,
      title: "Request Aborted",
      detail: error instanceof Error ? error.message : "Request has been aborted"
    };
  }

  return {
    status: 503,
    type: ErrorCodes.client_network_error,
    title: "Network Error",
    detail: error instanceof Error ? error.message : "Network connection failed"
  };
}

const defaultFetch = async ({
  apiName,
  bearer,
  body = undefined,
  options = {},
  resultType = responseType.json
}: {
  apiName: keyof typeof apiNameMap,
  bearer: string,
  body?: any,
  options?: FetchOptions,
  resultType?: responseType
}) => {
  let error: HttpError | undefined = undefined;
  let response = undefined;

  const {
    method,
    url
  } = await createFetchDefaults({ apiName, options });

  const {
    headers
  } = options

  if (headers) {
    headers['Authorization'] = `Bearer ${bearer}`;
  }

  let defaultHeaders: Record<string, string> = {
    'Authorization': `Bearer ${bearer}`,
  }

  if (body) {
    defaultHeaders['Content-Type'] = 'application/json';
  }

  try {
    response = await fetch(url.href,
      {
        method,
        headers: headers ?? defaultHeaders,
        body: body,
        signal: options.abortSignal,
      });
  } catch (error_) {
    const problemDetails = createProblemDetails(error_);
    return { error: new HttpError(problemDetails) };
  }

  if (!response.ok) {
    // check for expired/invalid token
    if (response.status === 401 && apiName !== 'login') {
      if (!sessionExpiredHandling) {
        sessionExpiredHandling = true;
        // Capture path before logout() — reactive flush after logout can change currentRoute
        const redirectTo = router.currentRoute.value.fullPath;
        const { logout } = useAuth();
        logout();
        const { show } = useGlobalSnackbar();
        show('warning', [i18n.global.t('Login.SessionExpired')]);
        router.push({ name: Routes.login, query: { redirectTo } });
      }
      return {
        error: new HttpError({
          status: 401,
          type: ErrorCodes.client_session_expired,
          title: 'Session Expired',
          detail: 'Your session has expired. Please log in again.'
        })
      };
    }

    try {
      const data = await response.json();
      if (isProblemDetails(data)) {
        return { error: new HttpError(data) };
      }
      return {
        error: new HttpError({
          status: response.status,
          type: ErrorCodes.unknown,
          title: 'Unknown Error',
          detail: 'Error response is not in problem details format.'
        })
      };
    } catch (e) {
      return {
        error: new HttpError({
          status: response.status,
          type: ErrorCodes.unknown,
          title: 'Unknown Error',
          detail: 'Could not parse error response.'
        })
      };
    }
  }

  const paginationLimit = Number.parseInt(response.headers.get('Pagination-Limit') ?? '10', 10) ?? 0;
  const paginationCount = Number.parseInt(response.headers.get('Pagination-Count') ?? '10', 10) ?? 0;
  const paginationPage = Number.parseInt(response.headers.get('Pagination-Page') ?? '1', 10) ?? 0;
  const totalItemCount = Number.parseInt(response.headers.get('Total-Item-Count') ?? '0', 10) ?? 0;


  let result: any;
  try {
    switch (resultType) {
      case responseType.json: {
        result = await response.json();
        break;
      }
      case responseType.text: {
        result = await response.text();
        break;
      }
      default: {
        result = await response.json();
      }
    }
  } catch (e) { /* Ignored */
  }

  return {
    error,
    paginationCount,
    paginationLimit,
    paginationPage,
    totalItemCount,
    response: result,
  }
};

const resetSessionExpiredHandling = () => {
  sessionExpiredHandling = false;
};

export {
  defaultFetch,
  resetSessionExpiredHandling,
}
